import React, { useState, useEffect, useCallback } from 'react';
import { getCrawlHistory, getCrawlHistoryDetail, crawlMatches } from '../api';
import { useSports } from '../hooks';
import { Pagination, Modal, AutoStatusBadge, Button } from './common';
import { toast } from 'react-toastify';
import { commonStyles, colors, typography, spacing } from '../styles/design-system';

interface CrawlHistoryData {
  history_id: number;
  request_type: string;
  sport_name: string;
  league_code: string | null;
  crawl_date: string | null;
  ai_provider: string | null;
  admin_id: number | null;
  notion_admin_id: number | null;
  admin_name: string | null;
  notion_admin_name: string | null;
  execution_start: string;
  execution_end: string | null;
  duration: number | null;
  status: string;
  total_matches: number;
  success_count: number;
  error_count: number;
  error_message: string | null;
  source: string | null;
  created_by: string | null;
  created_at: string;
  match_count: number;
  created_count: number;
  updated_count: number;
  failed_count: number;
}

// 상세 조회 응답 타입 (API 정의에서 파생)
type CrawlHistoryDetailData = Awaited<ReturnType<typeof getCrawlHistoryDetail>>['data'];

// error_message에서 경기전/실패 정보 파싱
interface UncollectedInfo {
  pre_match_count: number;
  failed_count: number;
  games: Array<{
    game_id: string;
    home_team: string;
    away_team: string;
    status: string;
    reason: string;
  }>;
}

const parseErrorMessage = (errorMessage: string | null): UncollectedInfo | null => {
  if (!errorMessage) return null;
  try {
    const parsed = JSON.parse(errorMessage);
    if (parsed.pre_match_count !== undefined || parsed.failed_count !== undefined) {
      return {
        pre_match_count: parsed.pre_match_count || 0,
        failed_count: parsed.failed_count || 0,
        games: parsed.games || []
      };
    }
    return null;
  } catch {
    return null;
  }
};

interface CrawlerHistoryProps {
  adminId?: number;
  initialData?: {
    sports: Record<string, {
      name: string;
      leagues: Array<{value: string; label: string}>;
    }>;
  } | null;
}

const CrawlerHistory: React.FC<CrawlerHistoryProps> = ({ adminId, initialData }) => {
  const [loading, setLoading] = useState(false);
  const [histories, setHistories] = useState<CrawlHistoryData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedSport, setSelectedSport] = useState<string>('');
  const [selectedDetail, setSelectedDetail] = useState<CrawlHistoryDetailData | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [recrawling, setRecrawling] = useState<string | null>(null);

  // useSports 훅 사용
  const { sportOptions: hookSportOptions, getSportLabelByName } = useSports();

  const fetchHistories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCrawlHistory({
        page: currentPage,
        pageSize: 10,
        sport: selectedSport || undefined
      });

      setHistories(response.data || []);
      setTotalPages(response.pagination?.total_pages || 0);
      setTotalItems(response.pagination?.total_items || 0);
    } catch (error) {
      console.error('Failed to fetch crawl histories:', error);
      toast.error('크롤링 내역을 불러오는데 실패했음.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedSport]);

  useEffect(() => {
    fetchHistories();
  }, [fetchHistories]);

  const showDetails = async (historyId: number) => {
    setSelectedDetail(null);
    setDetailLoading(true);
    setShowDetailModal(true);
    try {
      const response = await getCrawlHistoryDetail(historyId);
      setSelectedDetail(response.data);
    } catch (error) {
      toast.error('상세 정보를 불러오는데 실패했음.');
      setShowDetailModal(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRecrawl = async (sport: string, date: string | null) => {
    if (!date) {
      toast.error('크롤링 날짜 정보가 없음.');
      return;
    }

    setRecrawling(sport);
    try {
      await crawlMatches(sport, undefined, date, undefined, adminId);
      toast.success(`${getSportLabel(sport)} 재크롤링이 완료되었음.`);
      fetchHistories();
    } catch (error) {
      toast.error(`${getSportLabel(sport)} 재크롤링에 실패했음.`);
    } finally {
      setRecrawling(null);
    }
  };

  // useSports 훅에서 가져온 데이터 사용 (initialData fallback)
  const getSportLabel = (sport: string): string => {
    // useSports 훅 사용
    const label = getSportLabelByName(sport);
    if (label !== sport) return label;
    // initialData fallback
    if (initialData?.sports && initialData.sports[sport]) {
      return initialData.sports[sport].name;
    }
    return sport;
  };

  // 종목 옵션 목록 - useSports 훅 우선
  const sportOptions = hookSportOptions.length > 0
    ? hookSportOptions
    : (initialData?.sports
      ? Object.entries(initialData.sports).map(([value, data]) => ({ value, label: data.name }))
      : []);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ko-KR');
  };

  // 백엔드 duration 컬럼은 초 단위
  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}초`;
    return `${Math.floor(seconds / 60)}분 ${seconds % 60}초`;
  };

  const getRequestTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      'crawl': '크롤링',
      'query': 'DB 조회',
      'scheduler': '스케줄러'
    };
    return typeMap[type] || type;
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>크롤링 내역</h3>
      </div>

      <div style={styles.filters}>
        <select
          value={selectedSport}
          onChange={(e) => {
            setSelectedSport(e.target.value);
            setCurrentPage(1);
          }}
          style={styles.select}
        >
          <option value="">전체 종목</option>
          {sportOptions.map(sport => (
            <option key={sport.value} value={sport.value}>{sport.label}</option>
          ))}
        </select>
      </div>

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={styles.emptyState}>로딩 중...</div>
        ) : histories.length === 0 ? (
          <div style={styles.emptyState}>크롤링 내역이 없음.</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>실행 시간</th>
                <th style={styles.th}>요청 타입</th>
                <th style={styles.th}>종목</th>
                <th style={styles.th}>날짜</th>
                <th style={styles.th}>프롬프트</th>
                <th style={styles.th}>노션</th>
                <th style={styles.th}>상태</th>
                <th style={styles.th}>전체</th>
                <th style={styles.th}>수집/경기전/실패</th>
                <th style={styles.th}>소요시간</th>
                <th style={styles.th}>상세</th>
              </tr>
            </thead>
            <tbody>
              {histories.map((history) => (
                <tr key={history.history_id}>
                  <td style={styles.td}>{formatDate(history.execution_start)}</td>
                  <td style={styles.td}>{getRequestTypeLabel(history.request_type)}</td>
                  <td style={styles.td}>{getSportLabel(history.sport_name)}</td>
                  <td style={styles.td}>{history.crawl_date || '-'}</td>
                  <td style={styles.td}>{history.admin_name || '-'}</td>
                  <td style={styles.td}>{history.notion_admin_name || '-'}</td>
                  <td style={styles.td}>
                    <AutoStatusBadge status={history.status} />
                  </td>
                  <td style={styles.td}>{history.total_matches}</td>
                  <td style={styles.td}>
                    {(() => {
                      const uncollectedInfo = parseErrorMessage(history.error_message);
                      if (uncollectedInfo) {
                        return (
                          <div style={styles.countContainer}>
                            <span style={styles.successCount}>{history.success_count}</span>
                            /
                            <span style={uncollectedInfo.pre_match_count > 0 ? styles.preMatchCountHighlight : styles.preMatchCount}>
                              {uncollectedInfo.pre_match_count}
                            </span>
                            /
                            <span style={styles.errorCount}>{uncollectedInfo.failed_count}</span>
                          </div>
                        );
                      }
                      return (
                        <>
                          <span style={styles.successCount}>{history.success_count}</span>
                          /
                          <span style={styles.preMatchCount}>-</span>
                          /
                          <span style={styles.errorCount}>{history.error_count}</span>
                        </>
                      );
                    })()}
                  </td>
                  <td style={styles.td}>{formatDuration(history.duration)}</td>
                  <td style={styles.td}>
                    <div style={styles.actionButtons}>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => showDetails(history.history_id)}
                      >
                        상세
                      </Button>
                      {(() => {
                        const uncollectedInfo = parseErrorMessage(history.error_message);
                        if (uncollectedInfo && uncollectedInfo.pre_match_count > 0) {
                          return (
                            <Button
                              variant="warning"
                              size="sm"
                              onClick={() => handleRecrawl(history.sport_name, history.crawl_date)}
                              loading={recrawling === history.sport_name}
                              title="경기전 상태 경기 재수집"
                            >
                              재수집
                            </Button>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 0 && (
        <Pagination
          pagination={{
            currentPage,
            totalPages,
            totalItems,
            hasNext: currentPage < totalPages,
            hasPrev: currentPage > 1
          }}
          onPageChange={setCurrentPage}
          loading={loading}
        />
      )}

      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="크롤링 상세 정보"
        size="lg"
      >
        {detailLoading || !selectedDetail ? (
          <div style={styles.emptyState}>상세 정보를 불러오는 중...</div>
        ) : (
          <>
            <div style={styles.detailSection}>
              <h4 style={styles.detailSectionTitle}>담당자 정보</h4>
              <div style={styles.detailInfo}>
                <div style={styles.detailRow}>
                  <strong>프롬프트 담당자:</strong> {selectedDetail.history.admin_name || '-'}
                </div>
                <div style={styles.detailRow}>
                  <strong>노션 담당자:</strong> {selectedDetail.history.notion_admin_name || '-'}
                </div>
                <div style={styles.detailRow}>
                  <strong>종목:</strong> {getSportLabel(selectedDetail.history.sport_name)}
                </div>
                <div style={styles.detailRow}>
                  <strong>AI 모델:</strong> {selectedDetail.history.ai_provider || '크롤링만'}
                </div>
                <div style={styles.detailRow}>
                  <strong>요청 타입:</strong> {getRequestTypeLabel(selectedDetail.history.request_type)}
                </div>
                <div style={styles.detailRow}>
                  <strong>수집 날짜:</strong> {selectedDetail.history.crawl_date || '-'}
                </div>
              </div>
            </div>

            <div style={styles.detailSection}>
              <h4 style={styles.detailSectionTitle}>기본 정보</h4>
              <div style={styles.detailInfo}>
                <div style={styles.detailRow}><strong>실행 시간:</strong> {formatDate(selectedDetail.history.execution_start)}</div>
                <div style={styles.detailRow}><strong>종료 시간:</strong> {formatDate(selectedDetail.history.execution_end)}</div>
                <div style={styles.detailRow}><strong>소요 시간:</strong> {formatDuration(selectedDetail.history.duration)}</div>
                <div style={styles.detailRow}><strong>상태:</strong> <AutoStatusBadge status={selectedDetail.history.status} /></div>
                <div style={styles.detailRow}>
                  <strong>수집 현황:</strong>{' '}
                  <span style={styles.successCount}>{selectedDetail.history.success_count}</span> 수집 완료
                  {(() => {
                    const uncollectedInfo = parseErrorMessage(selectedDetail.history.error_message);
                    if (uncollectedInfo) {
                      return (
                        <>
                          {' / '}
                          <span style={styles.preMatchCount}>{uncollectedInfo.pre_match_count}</span> 경기전
                          {' / '}
                          <span style={styles.errorCount}>{uncollectedInfo.failed_count}</span> 실패
                        </>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </div>

            {/* 미수집 경기 목록 (경기전 + 실패) */}
            {(() => {
              const uncollectedInfo = parseErrorMessage(selectedDetail.history.error_message);
              if (uncollectedInfo && uncollectedInfo.games && uncollectedInfo.games.length > 0) {
                return (
                  <div style={styles.detailSection}>
                    <div style={styles.detailSectionHeader}>
                      <h4 style={styles.detailSectionTitle}>미수집 경기 목록 ({uncollectedInfo.games.length}개)</h4>
                      {uncollectedInfo.pre_match_count > 0 && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            handleRecrawl(selectedDetail.history.sport_name, selectedDetail.history.crawl_date);
                          }}
                          loading={recrawling === selectedDetail.history.sport_name}
                        >
                          재크롤링
                        </Button>
                      )}
                    </div>
                    <div style={styles.matchResults}>
                      {uncollectedInfo.games.map((game, index) => (
                        <div key={`${game.game_id}-${index}`} style={styles.matchResultItem}>
                          <div style={styles.matchTeams}>
                            {game.home_team} vs {game.away_team}
                          </div>
                          <div style={styles.matchStatus}>
                            <span style={game.status === 'pre_match' ? styles.preMatchCount : styles.errorCount}>
                              {game.status === 'pre_match' ? '경기전' : '실패'}
                            </span>
                          </div>
                          <div style={styles.matchError}>{game.reason}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {selectedDetail.match_results && selectedDetail.match_results.length > 0 && (
              <div style={styles.detailSection}>
                <h4 style={styles.detailSectionTitle}>
                  수집한 경기 목록 ({selectedDetail.match_results.length}개)
                </h4>
                <div style={styles.matchResultsTable}>
                  <table style={styles.matchTable}>
                    <thead>
                      <tr>
                        <th style={styles.matchTh}>경기 ID</th>
                        <th style={styles.matchTh}>홈팀</th>
                        <th style={styles.matchTh}>원정팀</th>
                        <th style={styles.matchTh}>처리</th>
                        <th style={styles.matchTh}>저장 상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedDetail.match_results.map((result: any) => (
                        <tr key={result.result_id}>
                          <td style={styles.matchTd}>{result.game_id || '-'}</td>
                          <td style={styles.matchTd}>{result.home_team}</td>
                          <td style={styles.matchTd}>{result.away_team}</td>
                          <td style={styles.matchTd}>
                            <AutoStatusBadge
                              status={result.action_type}
                              labelMap={{
                                created: '생성',
                                updated: '업데이트',
                                skipped: '스킵',
                                failed: '실패'
                              }}
                            />
                          </td>
                          <td style={styles.matchTd}>
                            <div style={styles.savedBadgeContainer}>
                              {result.db_saved && <span style={styles.savedBadge}>DB</span>}
                              {result.prediction_saved && <span style={styles.savedBadge}>예측</span>}
                              {result.notion_saved && <span style={styles.savedBadge}>Notion</span>}
                              {!result.db_saved && !result.prediction_saved && !result.notion_saved && '-'}
                            </div>
                            {result.error_message && (
                              <div style={styles.matchError}>{result.error_message}</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Button
              variant="secondary"
              onClick={() => setShowDetailModal(false)}
              style={{ marginTop: spacing[5] }}
            >
              닫기
            </Button>
          </>
        )}
      </Modal>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    ...commonStyles.section,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing[5],
  },
  title: {
    ...commonStyles.sectionTitle,
    margin: 0,
  },
  filters: {
    display: 'flex',
    gap: spacing[3],
    marginBottom: spacing[5],
  },
  select: {
    ...commonStyles.select,
  },
  tableContainer: {
    ...commonStyles.tableContainer,
  },
  table: {
    ...commonStyles.table,
  },
  th: {
    ...commonStyles.th,
  },
  td: {
    ...commonStyles.td,
  },
  successCount: {
    color: colors.status.success,
    fontWeight: typography.fontWeight.medium,
  },
  preMatchCount: {
    color: colors.status.warning,
    fontWeight: typography.fontWeight.medium,
  },
  preMatchCountHighlight: {
    color: colors.status.warning,
    fontWeight: typography.fontWeight.bold,
  },
  countContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
  },
  actionButtons: {
    display: 'flex',
    gap: spacing[2],
    alignItems: 'center',
  },
  errorCount: {
    color: colors.status.error,
    fontWeight: typography.fontWeight.medium,
  },
  emptyState: {
    ...commonStyles.emptyState,
  },
  detailSection: {
    marginBottom: spacing[7],
  },
  detailSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing[4],
  },
  detailSectionTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    color: colors.gray[800],
    margin: 0,
  },
  detailInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[2],
  },
  detailRow: {
    fontSize: typography.fontSize.base,
    color: colors.gray[700],
  },
  errorMessage: {
    color: colors.status.error,
    marginTop: spacing[3],
    fontSize: typography.fontSize.sm,
  },
  matchResults: {
    maxHeight: '300px',
    overflowY: 'auto',
  },
  matchResultsTable: {
    maxHeight: '400px',
    overflowY: 'auto',
    border: `1px solid ${colors.gray[200]}`,
  },
  matchTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: typography.fontSize.sm,
  },
  matchTh: {
    padding: `${spacing[2]} ${spacing[3]}`,
    textAlign: 'left',
    backgroundColor: colors.gray[100],
    borderBottom: `1px solid ${colors.gray[200]}`,
    fontWeight: typography.fontWeight.semibold,
    color: colors.gray[700],
    position: 'sticky' as const,
    top: 0,
  },
  matchTd: {
    padding: `${spacing[2]} ${spacing[3]}`,
    borderBottom: `1px solid ${colors.gray[100]}`,
    color: colors.gray[700],
    verticalAlign: 'top',
  },
  matchResultItem: {
    padding: spacing[4],
    border: `1px solid ${colors.gray[200]}`,
    marginBottom: spacing[3],
  },
  matchTeams: {
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing[2],
    color: colors.gray[800],
  },
  matchStatus: {
    display: 'flex',
    gap: spacing[3],
    alignItems: 'center',
  },
  actionType: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  savedBadgeContainer: {
    display: 'flex',
    gap: spacing[2],
    flexWrap: 'wrap' as const,
  },
  savedBadge: {
    fontSize: typography.fontSize.xs,
    color: colors.white,
    backgroundColor: colors.gray[700],
    padding: `${spacing[1]} ${spacing[2]}`,
    fontWeight: typography.fontWeight.medium,
  },
  matchError: {
    color: colors.status.error,
    fontSize: typography.fontSize.xs,
    marginTop: spacing[1],
  },
};

export default CrawlerHistory;
